import AlertError from "@components/AlertError";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import t from "@ext/localization/locale/translate";
import getComponents from "@ext/markdown/core/render/components/getComponents/getComponents";
import Renderer from "@ext/markdown/core/render/components/Renderer";
import type { RenderableTreeNodes } from "@ext/markdown/core/render/logic/Markdoc";
import type { FragmentError } from "@ext/markdown/elements/fragment/edit/model/types";
import getFragmentScope from "@ext/markdown/elements/fragment/logic/getFragmentScope";
import getFragmentErrorMessage from "@ext/markdown/elements/fragment/render/logic/getFragmentErrorMessage";
import type { JSONContent } from "@tiptap/core";
import { createContext, useContext, useMemo } from "react";

const fragmentClassName =
	"-mx-2 -mt-1 mb-[0.2em] px-2 py-1 [&_*[data-focusable=true]]:![outline-offset:unset] [&_*[data-focusable=true]]:![outline:unset]";

interface FragmentProps {
	id: string;
	type?: string;
	owner?: string;
	content: JSONContent | RenderableTreeNodes;
	error?: FragmentError;
	isPrint?: boolean;
}

// A fragment nested in another one was looked up in the catalog that owns the outer one, and so are its resources.
const EnclosingFragmentOwner = createContext<string | undefined>(undefined);

/** A fragment whose scope is still loading waits in a neutral note: nothing has gone wrong yet. */
const FragmentStandIn = ({ error }: { error?: FragmentError }) => {
	const message = getFragmentErrorMessage(error);
	if (error?.reason === "scope-not-ready" && error.state === "loading")
		return (
			<div className="mb-[0.2em] rounded-lg border border-dashed border-secondary-border px-4 py-3 text-muted">
				{message}
			</div>
		);
	return <AlertError error={{ message }} title={t("fragment-render-error")} />;
};

const Fragment = (props: FragmentProps) => {
	const { id, type, owner, content, error, isPrint } = props;
	const lookupCatalog = useContext(EnclosingFragmentOwner);
	const contents = useMemo(() => Renderer(content, { components: getComponents() }, isPrint), [content, isPrint]);

	return content ? (
		<ResourceService.Provider
			articleCatalog={lookupCatalog}
			articleScope={getFragmentScope(type)}
			id={id}
			provider="fragment"
		>
			<EnclosingFragmentOwner.Provider value={owner}>
				<div
					className={fragmentClassName}
					data-component="fragment"
					data-focusable="true"
					data-fragment-id={id}
					data-iseditable={false}
				>
					{contents}
				</div>
			</EnclosingFragmentOwner.Provider>
		</ResourceService.Provider>
	) : (
		<FragmentStandIn error={error} />
	);
};

export default Fragment;
